import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Switch,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useRecoilState, useRecoilValue } from "recoil";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FlatSheetKey,
  inflatedTabsFamily,
} from "@/features/line-sheet-sets/helpers/sheet-state";
import { uniq } from "lodash";
import AppSelect from "@/features/line-sheet-sets/app-select";
import { SheetProduct } from "@/features/order-sheets/sheet.type";
import { OrderSheetSetPagePersistedQuery } from "@/features/order-sheet-sets/hooks/use-order-sheet-set-page-persisted-query";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  isSheetProduct,
  LineSheetProduct,
} from "@/features/line-sheet-sets/line-sheet-set.types";
import SVG from "react-inlinesvg";
import FilterModal from "@/features/order-sheet-sets/FilterModal";
import { NavigateOptions } from "react-router";
import ChipList from "@/features/order-sheet-sets/ChipList";
import { orderSheetPageSearchKeywordStore } from "@/features/order-sheet-sets/recoils/order-sheet-page-store";
import useGetLineSheetSetFilterInfo from "@/features/line-sheet-sets/hooks/use-get-line-sheet-set-filter-info";
import useIdentity from "@/features/ui/hooks/use-identity";
import AppQueryControllerAutoComplete from "@/features/ui/app-query-controller-auto-complete";
import { Suggestion } from "@/features/ui/app-autocomplete-input";
import { Company } from "@/features/types";
import { matchesSearchKeyword } from "@/utils/search-text";

export interface SheetTabFilterQuery {
  brandName__eq?: string[];
  // ID 기반 브랜드 필터 — 표준명/원본명 모호함을 피하고 브랜드 회사 id로 정확히 매칭.
  // brandId__in이 설정되면 brandName__eq 대신 이쪽을 사용.
  brandId__in?: number[];
  gender__eq?: string[];
  category__eq?: string[];
  season__in?: string[];
  quantity__gte?: number;
  query?: string;
  "madeByCompany.id__in"?: number[];
  orderBy?: string;
}

export const EMPTY_SHEET_TAB_FILTER_QUERY: SheetTabFilterQuery = {
  brandName__eq: undefined,
  brandId__in: undefined,
  gender__eq: undefined,
  category__eq: undefined,
  quantity__gte: undefined,
  query: undefined,
  season__in: undefined,
};

// 검색/필터가 하나라도 걸려 있는지 — 그리드·갤러리가 '검색/필터한 상품 n' 표시 여부를
// 같은 기준으로 판단하도록 한곳에 둔다.
export function isSheetTabFilterActive(query: SheetTabFilterQuery): boolean {
  return Boolean(
    (query.brandName__eq && query.brandName__eq.length > 0) ||
      query.gender__eq ||
      query.category__eq ||
      query.season__in ||
      query.query
  );
}

interface OrderSheetTabFilterProps {
  sheetKey: FlatSheetKey;
  lineSheetSetId: number;
  lineSheetId?: number;
  tabIndex?: number;
  query: OrderSheetSetPagePersistedQuery;
  setQuery: (
    updater: (
      prev: OrderSheetSetPagePersistedQuery
    ) => OrderSheetSetPagePersistedQuery,
    navigateOpts?: NavigateOptions | undefined
  ) => void;
  onFilterSelect: (query: SheetTabFilterQuery) => void;
  // onSelectAllClick: (value: boolean) => void;
  // isAllSelected: boolean;
  onClear: () => void;
  queryCount?: number;
  setQueryCount?: (count: number) => void;
  // 제공되면 브랜드 picker가 ID 기반으로 동작 — value는 회사 id, query.brandId__in에 저장.
  // 미제공이면 기존 이름 기반(brandName__eq) 동작 유지.
  brandCompanies?: Company[];
  // '수량 입력 상품만 보기'를 필터 줄에 함께 둘지. 자체 토글을 가진 화면(에이전시 주문서)은 끈다.
  showQuantityToggle?: boolean;
  // '관심 상품만 보기' — 값은 페이지가 쥔다(쿼리가 아니라 화면 상태). 넘기면 시즌 옆에 스위치가 붙고,
  // 초기화 버튼도 이 값을 함께 끈다.
  likedOnly?: boolean;
  onLikedOnlyChange?: (checked: boolean) => void;
  // 필터 첫 줄 오른쪽 끝에 고정해 둘 요소(바이어 주문서의 문의/답변 버튼 등)
  rightSlot?: React.ReactNode;
}

// 필터 줄에 늘어놓는 on/off — 라벨 + 스위치 한 쌍이 반복돼서 묶어 둔다.
function FilterSwitch({
  id,
  label,
  isChecked,
  onChange,
}: {
  id: string;
  label: string;
  isChecked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <FormControl display={"flex"} alignItems={"center"} width={"fit-content"}>
      <FormLabel htmlFor={id} mb={"0"} mr={"6px"}>
        <Text textStyle={"body-sm-regular"} whiteSpace={"nowrap"}>
          {label}
        </Text>
      </FormLabel>
      <Switch
        id={id}
        isChecked={isChecked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </FormControl>
  );
}

// 주문 수량·신규 스탁 수량·확정 수량 중 하나라도 minimum 이상인 상품인지.
// '수량 입력 상품만 보기' 토글과 브랜드 필터의 주문 뱃지·정렬이 같은 기준을 쓴다.
export function hasProductQuantity(
  product: SheetProduct,
  minimum: number = 1
): boolean {
  return [
    product.orderQuantityWithOptionList,
    product.newStockWithOptionList,
    product.confirmedQuantityWithOptionList,
  ].some((list) => list?.some((i) => i.quantity >= minimum));
}

export function isProductFiltered(
  product: SheetProduct | LineSheetProduct,
  query?: SheetTabFilterQuery
) {
  if (query) {
    const predicates = [
      (p: SheetProduct | LineSheetProduct) => {
        return (
          !query?.gender__eq ||
          query?.gender__eq.length === 0 ||
          query?.gender__eq.includes(p.gender ?? "")
        );
      },
      (p: SheetProduct | LineSheetProduct) => {
        // brandId__in 우선 — 설정돼 있으면 brand 회사 id로만 매칭.
        // brandName__eq는 표준명/원본명 모호함이 있어 가능하면 ID 매칭을 사용.
        if (query?.brandId__in && query.brandId__in.length > 0) {
          return query.brandId__in.includes(p.brandId ?? -1);
        }
        return (
          !query?.brandName__eq ||
          query?.brandName__eq.length === 0 ||
          query?.brandName__eq.includes(p.brandName ?? "")
        );
      },
      (p: SheetProduct | LineSheetProduct) => {
        return (
          !query?.season__in ||
          query?.season__in.length === 0 ||
          query?.season__in.includes(p.season ?? "")
        );
      },
      (p: SheetProduct | LineSheetProduct) => {
        return (
          !query?.category__eq ||
          query?.category__eq.length === 0 ||
          (p.categories.length > 0 &&
            query?.category__eq.some((category) =>
              p.categories.includes(category ?? "")
            ))
        );
      },
      (p: SheetProduct | LineSheetProduct) => {
        if (isSheetProduct(p)) {
          return (
            !query?.quantity__gte || hasProductQuantity(p, query.quantity__gte)
          );
        } else {
          return true;
        }
      },
      (p: SheetProduct | LineSheetProduct) => {
        if (!query?.query) return true;
        const keyword = query.query;
        return (
          matchesSearchKeyword(p.sku, keyword) ||
          matchesSearchKeyword(
            p.note
              ? Object.values(p.note)
                  .join(" ")
                  .replaceAll(/\s{2,}/g, " ")
              : undefined,
            keyword
          ) ||
          matchesSearchKeyword(p.brandName, keyword) ||
          matchesSearchKeyword(p.gender, keyword) ||
          matchesSearchKeyword(p.season, keyword) ||
          p.categories
            .filter(Boolean)
            .some((category) => matchesSearchKeyword(category, keyword)) ||
          matchesSearchKeyword(p.name, keyword) ||
          matchesSearchKeyword(p.colorName, keyword) ||
          matchesSearchKeyword(p.colorCode, keyword)
        );
      },
    ];
    return predicates.every((predicate) => predicate(product));
  }

  return true;
}

// isProductFiltered / filterInflatedProducts가 실제로 읽는 필드만 뽑아,
// 내용이 같으면 같은 객체 정체성을 유지시킨다.
//
// persisted query(value)는 searchParams가 바뀌면 무조건 새 객체라, referer나
// revision_number처럼 필터와 무관한 키가 바뀌어도 전 상품 재계산(정렬 + 새 객체 생성)이
// 한 바퀴 돈다. 그걸 막기 위한 것.
//
// !! 위 isProductFiltered에 필터 조건을 추가하면 이 목록에도 반드시 추가할 것.
//    빠뜨리면 그 필터가 URL에서 바뀌어도 상품 목록이 갱신되지 않는다.
export function useStableSheetTabFilterQuery(
  query: SheetTabFilterQuery
): SheetTabFilterQuery {
  const next: SheetTabFilterQuery = {
    brandName__eq: query.brandName__eq,
    brandId__in: query.brandId__in,
    gender__eq: query.gender__eq,
    category__eq: query.category__eq,
    season__in: query.season__in,
    quantity__gte: query.quantity__gte,
    query: query.query,
    // 필터는 아니지만 filterInflatedProducts의 정렬 인자로 함께 쓰인다.
    orderBy: query.orderBy,
  };
  const key = JSON.stringify(next);

  // key가 같은 동안에는 첫 렌더에서 만든 객체를 그대로 돌려준다.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => next, [key]);
}

export default function SheetTabFilter({
  sheetKey,
  lineSheetSetId,
  lineSheetId = undefined,
  tabIndex = undefined,
  query,
  setQuery,
  onFilterSelect,
  onClear,
  queryCount = 0,
  setQueryCount,
  brandCompanies,
  showQuantityToggle = false,
  likedOnly = false,
  rightSlot,
  onLikedOnlyChange,
}: OrderSheetTabFilterProps) {
  const { t, tTitle } = useI18nHelper();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const identity = useIdentity();

  const { data } = useGetLineSheetSetFilterInfo(
    tabIndex !== -1
      ? {
          by: identity?.company?.type!,
          companyId: identity?.company?.id!,
        }
      : null,
    {
      lineSheetSetId: lineSheetSetId,
      lineSheetId: lineSheetId,
      lineSheetTabIndex: tabIndex,
    },
    {
      revalidateOnFocus: false,
    }
  );

  const seasonOptions =
    data?.seasons.map((season) => ({
      name: season,
      value: season,
    })) || [];
  const genderOptions =
    data?.genders.map((gender) => ({
      name: gender,
      value: gender,
    })) || [];
  const categoryOptions =
    data?.categories.map((category) => ({
      name: category,
      value: category,
    })) || [];

  const inflatedTabsState = useRecoilValue(inflatedTabsFamily(sheetKey));

  // 수량이 들어간 상품의 브랜드 — 브랜드 필터 목록에서 이 브랜드들을 맨 위로 올린다.
  // 주문하던 브랜드로 다시 돌아가기 쉽게 하려는 것이고, 그 안팎의 순서(우선순위·이름순)는 건드리지 않는다.
  const orderedBrandKeys = useMemo(() => {
    const names = new Set<string>();
    const ids = new Set<number>();
    inflatedTabsState?.tabs?.forEach((tab) => {
      tab.products?.forEach((p) => {
        if (!hasProductQuantity(p)) return;
        if (p.brandName) names.add(p.brandName);
        if (typeof p.brandId === "number") ids.add(p.brandId);
      });
    });
    return { names, ids };
  }, [inflatedTabsState]);

  // 수량이 들어간 브랜드 옵션 이름 왼쪽에 붙는 '수량 입력' 뱃지 — 목록 위로 올라온 이유를 눈으로 보여 준다.
  const orderedBrandBadge = useMemo(
    () => (
      <Text
        as={"span"}
        flexShrink={0}
        fontSize={"10px"}
        lineHeight={"16px"}
        fontWeight={"700"}
        color={"#1272EF"}
        background={"#E8F1FD"}
        borderRadius={"4px"}
        padding={"0 6px"}
      >
        {tTitle("quantity_entered")}
      </Text>
    ),
    [tTitle]
  );
  const toBrandOption = useCallback(
    (
      name: string,
      value: string,
      isOrdered: boolean
    ): AppSelectOption<string> => ({
      name,
      value,
      component: isOrdered ? (
        <Flex flex={1} minWidth={0} alignItems={"center"} gap={"6px"}>
          {orderedBrandBadge}
          <Text>{name}</Text>
        </Flex>
      ) : undefined,
    }),
    [orderedBrandBadge]
  );

  const putOrderedBrandsFirst = useCallback(
    <T extends { value: string }>(
      options: T[],
      isOrdered: (value: string) => boolean
    ): T[] => [
      ...options.filter((option) => isOrdered(option.value)),
      ...options.filter((option) => !isOrdered(option.value)),
    ],
    []
  );

  const brandOptions = useMemo(() => {
    // 에이전시 쪽 옵션은 "원본명(표준명)" 꼴로 오므로 앞부분만 맞아도 같은 브랜드로 본다.
    const isOrdered = (value: string) =>
      orderedBrandKeys.names.has(value) ||
      Array.from(orderedBrandKeys.names).some((name) =>
        value.startsWith(`${name}(`)
      );
    const options =
      data?.brands.map((brand) =>
        toBrandOption(brand, brand, isOrdered(brand))
      ) || [];
    return putOrderedBrandsFirst(options, isOrdered);
  }, [data?.brands, orderedBrandKeys, putOrderedBrandsFirst, toBrandOption]);

  const allSKU = useMemo(
    () =>
      inflatedTabsState?.tabs
        ?.flatMap((tab) => tab.products?.map((product) => product.sku) || [])
        ?.filter((sku): sku is string => typeof sku === "string") || [],
    [inflatedTabsState]
  );

  const toast = useToast();

  let inputValue: Suggestion;
  if (query?.query) {
    inputValue = {
      value: query.query,
      type: "custom",
      label: query.query,
    };
  } else {
    inputValue = {
      value: "",
      type: "custom",
      label: "",
    };
  }

  // 렌더마다 수천 개짜리 배열을 새로 만들면 자동완성 쪽 effect 가 매번 돌고,
  // 그때마다 입력값이 초기화된다 — 원본이 바뀔 때만 다시 만든다.
  const suggestions: Suggestion[] = useMemo(() => {
    const brands: Suggestion[] = (data?.brands ?? []).map((brand) => ({
      type: "Brand",
      label: brand,
      value: brand,
    }));
    if (allSKU.length >= 3000) return brands;
    return brands.concat(
      allSKU.map((sku) => ({ type: "SKU", label: sku, value: sku }))
    );
  }, [data?.brands, allSKU]);

  const [orderSheetSearchKeyword, setOrderSheetSearchKeyword] = useRecoilState(
    orderSheetPageSearchKeywordStore
  );

  useEffect(() => {
    setOrderSheetSearchKeyword(query.query ?? "");
  }, [query.query, setOrderSheetSearchKeyword]);

  const handleSelect = useCallback(
    (key: string, value: string | string[] | number | number[] | undefined) => {
      onFilterSelect({
        ...query,
        [key]: value,
      });
    },
    [query, onFilterSelect]
  );

  // brandCompanies가 제공되면 ID 기반 브랜드 옵션 생성.
  // 이름 매칭(name vs orgName)으로는 누락 위험이 커서 — 실제 products의 brandId를
  // 기준으로 교집합을 잡음. 인플레이트된 product가 아직 없으면 전체 노출.
  const brandIdOptions = React.useMemo(() => {
    if (!brandCompanies) return undefined;
    const presentBrandIds = new Set<number>();
    inflatedTabsState?.tabs?.forEach((tab) => {
      tab.products?.forEach((p: any) => {
        if (typeof p?.brandId === "number") {
          presentBrandIds.add(p.brandId);
        }
      });
    });
    const filtered =
      presentBrandIds.size > 0
        ? brandCompanies.filter((c) => presentBrandIds.has(c.id))
        : brandCompanies;
    return putOrderedBrandsFirst(
      filtered.map((c) =>
        toBrandOption(c.name, String(c.id), orderedBrandKeys.ids.has(c.id))
      ),
      (value) => orderedBrandKeys.ids.has(Number(value))
    );
  }, [
    brandCompanies,
    inflatedTabsState,
    orderedBrandKeys,
    putOrderedBrandsFirst,
    toBrandOption,
  ]);

  return (
    // 검색·필터 입력은 첫 줄, 걸린 필터(초기화 + 뱃지)는 그 아랫줄 — 한 줄에 몰아 두면
    // 뱃지가 오른쪽으로 밀려 잘리거나 스크롤돼 무엇을 걸었는지 한눈에 안 들어온다.
    <Flex flexDirection={"column"} gap={"8px"} width={"100%"}>
      <Flex
        gap={"12px"}
        flexDirection={"row"}
        alignItems={"center"}
        width={"100%"}
      >
        {/*<AppCheckBox*/}
        {/*  size={"small"}*/}
        {/*  isChecked={isAllSelected}*/}
        {/*  onChange={onSelectAllClick}*/}
        {/*/>*/}
        <Box position={"relative"}>
          <AppQueryControllerAutoComplete
            key={"AgencyLineSheetSetListFilter_Query"}
            value={
              inputValue.type === "custom"
                ? inputValue
                : {
                    value: "",
                    type: "custom",
                    label: "",
                  }
            }
            showButton={true}
            placeholder={t("search_input_order_sheet_placeholder")}
            leadingIcon="search"
            showIconsAndBtn={true}
            width="400px"
            suggestions={suggestions}
            onChange={(value) => {
              if (
                value.value &&
                value.type !== "Brand" &&
                value.type !== "Boutique" &&
                value.value.length < 3
              ) {
                toast({
                  title: "검색어는 3글자 이상 입력해주세요.",
                  status: "error",
                  duration: 3000,
                  isClosable: true,
                  position: "top",
                });
                return;
              }
              setQuery(
                (prev) => {
                  if (value.type === "Brand") {
                    // 이미 선택된 브랜드일 경우 무시
                    if (
                      prev["brandName__eq"]
                        ?.map((id) => String(id))
                        .includes(value.value)
                    ) {
                      return prev;
                    }
                    return {
                      ...prev,
                      brand_name__eq: uniq([
                        ...(prev["brandName__eq"] || []),
                        value.value,
                      ]),
                      query: "",
                      pageNumber: 1,
                    };
                  } else {
                    return {
                      ...prev,
                      query: value.value,
                      pageNumber: 1,
                    };
                  }
                },
                { replace: true }
              );
            }}
          />
        </Box>
        <Flex gap={"12px"}>
          <Flex flexDirection={"column"} gap={"6px"}>
            {brandIdOptions ? (
              // ID 기반 브랜드 picker — value/저장 모두 회사 id(문자열) 사용.
              <AppSelect<string>
                isSearchable={true}
                isMultiple={true}
                minWidth={"90px"}
                name={tTitle("brand")}
                value={query.brandId__in?.map((id) => String(id))}
                options={brandIdOptions}
                isDisabled={brandIdOptions.length === 0}
                showCount={true}
                isClearable={false}
                showSelectedBorder={true}
                showEmptyDataOption={true}
                onSelect={(value) => {
                  // 유효한 finite number id만 저장 — undefined/NaN이 URL로 새는 것 방지.
                  const toIds = (
                    raw: string | string[] | undefined
                  ): number[] | undefined => {
                    if (raw === undefined) return undefined;
                    const arr = Array.isArray(raw) ? raw : [raw];
                    const ids = arr
                      .filter((v) => v !== undefined && v !== null && v !== "")
                      .map((v) => Number(v))
                      .filter((n) => Number.isFinite(n));
                    return ids.length > 0 ? ids : undefined;
                  };
                  handleSelect("brandId__in", toIds(value));
                }}
              />
            ) : (
              <AppSelect<string>
                isSearchable={true}
                isMultiple={true}
                minWidth={"90px"}
                name={tTitle("brand")}
                value={query.brandName__eq}
                options={brandOptions}
                isDisabled={brandOptions.length === 0}
                showCount={true}
                isClearable={false}
                showSelectedBorder={true}
                showEmptyDataOption={true}
                onSelect={(value) => handleSelect("brandName__eq", value)}
              />
            )}
          </Flex>
          <Flex flexDirection={"column"} gap={"6px"}>
            <AppSelect<string>
              isSearchable={true}
              isMultiple={true}
              minWidth={"80px"}
              name={tTitle("gender")}
              value={query.gender__eq}
              options={genderOptions}
              isDisabled={genderOptions.length === 0}
              showCount={true}
              isClearable={false}
              showSelectedBorder={true}
              onSelect={(value) => handleSelect("gender__eq", value)}
            />
          </Flex>
          <Flex flexDirection={"column"} gap={"6px"}>
            <AppSelect<string>
              isSearchable={true}
              isMultiple={true}
              minWidth={"100px"}
              name={tTitle("category")}
              value={query.category__eq}
              showCount={true}
              isClearable={false}
              showSelectedBorder={true}
              isDisabled={
                categoryOptions.length === 0 ||
                categoryOptions.every(
                  (option) =>
                    option.value === "" ||
                    option.value === null ||
                    option.value === undefined
                )
              }
              options={categoryOptions.sort((a, b) =>
                a.name.localeCompare(b.name)
              )}
              onSelect={(value) => handleSelect("category__eq", value)}
            />
          </Flex>
          <Flex flexDirection={"column"} gap={"6px"}>
            <AppSelect<string>
              isSearchable={true}
              isMultiple={true}
              minWidth={"100px"}
              name={tTitle("season")}
              value={query.season__in}
              options={seasonOptions}
              showCount={true}
              isClearable={false}
              showSelectedBorder={true}
              isDisabled={seasonOptions.length === 0}
              onSelect={(value) => handleSelect("season__in", value)}
            />
          </Flex>
          {/* 보기 방식 스위치도 필터와 같은 줄에 — 초기화 한 번에 같이 풀린다. */}
          {showQuantityToggle && (
            <FilterSwitch
              id={"only_rows_with_qty"}
              label={t("only_rows_with_qty")}
              isChecked={query.quantity__gte === 1}
              onChange={(checked) =>
                handleSelect("quantity__gte", checked ? 1 : undefined)
              }
            />
          )}
          {onLikedOnlyChange && (
            <FilterSwitch
              id={"only_favorite_products"}
              label={t("only_favorite_products")}
              isChecked={likedOnly}
              onChange={onLikedOnlyChange}
            />
          )}
        </Flex>
        {rightSlot && (
          <Box marginLeft={"auto"} flexShrink={0}>
            {rightSlot}
          </Box>
        )}
      </Flex>
      {/*<Button*/}
      {/*  key={"FilterButton"}*/}
      {/*  padding={"4px 8px"}*/}
      {/*  height="28px"*/}
      {/*  display="flex"*/}
      {/*  gap={"4px"}*/}
      {/*  variant={"outline"}*/}
      {/*  onClick={() => setIsFilterModalOpen(true)}*/}
      {/*  minWidth={"82px"}*/}
      {/*  border={`${queryCount > 0 ? "1px solid #1272EF" : "1px solid #8F8F8C"}`}*/}
      {/*>*/}
      {/*  <SVG*/}
      {/*    src={"/icons-v1_1/filter.svg"}*/}
      {/*    width={"16px"}*/}
      {/*    height={"16px"}*/}
      {/*    color={"#444444"}*/}
      {/*    style={{*/}
      {/*      minWidth: "16px",*/}
      {/*    }}*/}
      {/*  />*/}
      {/*  <Text textStyle={"body-sm-bold"}>*/}
      {/*    {tTitle("filter")} {queryCount > 0 && queryCount}*/}
      {/*  </Text>*/}
      {/*</Button>*/}
      {(queryCount > 0 || query.query || query.quantity__gte || likedOnly) && (
        <Flex gap={"12px"} alignItems={"center"} width={"100%"} minWidth={0}>
          <AppButton
            key={"AgencyLineSheetSetListFilter_ResetFilter"}
            variant={"ghost"}
            size={"medium"}
            onClick={() => {
              setOrderSheetSearchKeyword("");
              onClear();
            }}
            width={"72px"}
            height={"28px"}
          >
            <Flex gap={"4px"} alignItems={"center"}>
              <SVG
                src={"/icons-v1_1/arrow-rotate.svg"}
                width={"16px"}
                height={"16px"}
                color={"#8F8F8C"}
              />
              <Text textStyle="body-sm-bold" color={"#444444"}>
                {t("clear_all")}
              </Text>
            </Flex>
          </AppButton>
          {queryCount > 0 && (
            <Text key={"Divider"} color={"#D4D4D1"}>
              |
            </Text>
          )}
          {/* 남는 폭 안에서만 뱃지가 가로 스크롤되도록 폭을 잡아 준다. */}
          <Box flex={1} minWidth={0}>
            <ChipList query={query} setQuery={setQuery} />
          </Box>
        </Flex>
      )}
      {/*<AppButton*/}
      {/*  variant={"normal"}*/}
      {/*  size={"medium"}*/}
      {/*  onClick={() => {*/}
      {/*    onSearchSubmit();*/}
      {/*  }}*/}
      {/*>*/}
      {/*  {tTitle("search")}*/}
      {/*</AppButton>*/}
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        setQueryCount={(count) => setQueryCount?.(count)}
        query={query}
        setQuery={setQuery}
        resetBtn={
          <AppButton
            key={"AgencyLineSheetSetListFilter_ResetFilter"}
            variant={"ghost"}
            size={"medium"}
            onClick={() => {
              setOrderSheetSearchKeyword("");
              onClear();
            }}
            width={"72px"}
            height={"28px"}
          >
            <Flex gap={"4px"} alignItems={"center"}>
              <SVG
                src={"/icons-v1_1/arrow-rotate.svg"}
                width={"16px"}
                height={"16px"}
                color={"#8F8F8C"}
              />
              <Text textStyle="body-sm-bold" color={"#444444"}>
                {t("clear_all")}
              </Text>
            </Flex>
          </AppButton>
        }
      >
        <Flex flexDirection="column" gap={"24px"}>
          <Flex flexDirection={"column"} gap={"6px"}>
            <Text>{tTitle("brand")}</Text>
            {brandIdOptions ? (
              <AppSelect<string>
                isSearchable={true}
                isMultiple={true}
                width={"120px"}
                name={tTitle("brand")}
                value={query.brandId__in?.map((id) => String(id))}
                options={brandIdOptions}
                isDisabled={brandIdOptions.length === 0}
                onSelect={(value) => {
                  // 유효한 finite number id만 저장 — undefined/NaN이 URL로 새는 것 방지.
                  const toIds = (
                    raw: string | string[] | undefined
                  ): number[] | undefined => {
                    if (raw === undefined) return undefined;
                    const arr = Array.isArray(raw) ? raw : [raw];
                    const ids = arr
                      .filter((v) => v !== undefined && v !== null && v !== "")
                      .map((v) => Number(v))
                      .filter((n) => Number.isFinite(n));
                    return ids.length > 0 ? ids : undefined;
                  };
                  handleSelect("brandId__in", toIds(value));
                }}
              />
            ) : (
              <AppSelect<string>
                isSearchable={true}
                isMultiple={true}
                width={"120px"}
                name={tTitle("brand")}
                value={query.brandName__eq}
                options={brandOptions}
                isDisabled={brandOptions.length === 0}
                onSelect={(value) => handleSelect("brandName__eq", value)}
              />
            )}
          </Flex>
          <Flex flexDirection={"column"} gap={"6px"}>
            <Text>{tTitle("season")}</Text>
            <AppSelect<string>
              isSearchable={true}
              isMultiple={true}
              width={"120px"}
              name={tTitle("season")}
              value={query.season__in}
              options={seasonOptions}
              isDisabled={seasonOptions.length === 0}
              onSelect={(value) => handleSelect("season__in", value)}
            />
          </Flex>
          <Flex flexDirection={"column"} gap={"6px"}>
            <Text>{tTitle("gender")}</Text>
            <AppSelect<string>
              isSearchable={true}
              isMultiple={true}
              width={"120px"}
              name={tTitle("gender")}
              value={query.gender__eq}
              options={genderOptions}
              isDisabled={genderOptions.length === 0}
              onSelect={(value) => handleSelect("gender__eq", value)}
            />
          </Flex>
          <Flex flexDirection={"column"} gap={"6px"}>
            <Text>{tTitle("category")}</Text>
            <AppSelect<string>
              isSearchable={true}
              isMultiple={true}
              width={"120px"}
              name={tTitle("category")}
              value={query.category__eq}
              isDisabled={
                categoryOptions.length === 0 ||
                categoryOptions.every(
                  (option) =>
                    option.value === "" ||
                    option.value === null ||
                    option.value === undefined
                )
              }
              options={categoryOptions.sort((a, b) =>
                a.name.localeCompare(b.name)
              )}
              onSelect={(value) => handleSelect("category__eq", value)}
            />
          </Flex>
        </Flex>
      </FilterModal>
    </Flex>
  );
}
